import { useEffect, useMemo, useState } from 'react';
import type { Campana, CanalCampana, ListaCampanas as Lista, Resultados, ResumenResultados } from '../../api/ops/campanas';
import { Boton } from '../../componentes/Boton';
import { Chip } from '../../componentes/Chip';
import { Desplegable } from '../../componentes/Desplegable';
import { EstadoCargando, EstadoError, EstadoNoContratado } from '../../componentes/EstadoPagina';
import { Filtros } from '../../componentes/Filtros';
import { Icono } from '../../componentes/Icono';
import { Paginacion } from '../../componentes/Paginacion';
import { Tabla } from '../../componentes/Tabla';
import { estadoDeError } from '../../logica/estados';
import { cifra, importe } from '../../logica/fechasCortas';
import { usePaginacionCursor } from '../../logica/paginacion';
import { useCabecera } from '../../marco/cabecera';
import { usePermisos } from '../../permisos/permisos';
import type { PropsApartado } from './Campanas';
import { Editor } from './Editor';
import { FichaCampana } from './FichaCampana';
import { cuandoDe, resultadoDe } from './lineas';
import { AYUDA, SUBTITULO, CANAL, cifraDeFiltro, ESTADO_CAMPANA, FILTROS_CAMPANAS, PAUSA, periodos } from './reglas';
import { Resumen } from './Resumen';

/**
 * 12.1 Campañas: la frase de resultados del periodo («Últimos 30 días: N reservas y compras…») que despliega el resumen
 * (12.4); filtros por estado con la cifra de `counts` (G802, solo si llega) y por canal; tabla con campaña, cuándo,
 * destinatarios, resultados y estado (chip solo para lo que pide actuar); «Anterior / Siguiente» sin totales (G121). La
 * ficha se abre con `?campana=` (también desde el aviso `campanas.campana_pausada`, G739, G763) y un borrador abre el
 * editor (12.2). El Encargado ve la lista y abre cada campaña sin acciones.
 */
export function ListaCampanas(props: PropsApartado) {
    const { ops, negocio, busqueda, ruta, navegar, movil, soloLectura, membresia, abrirAjustes, irA } = props;
    const { puede } = usePermisos();
    const crea = puede('campanas.campanas.crear') && !soloLectura;
    const veResumen = puede('campanas.resultados.ver_resumen');
    const [filtro, setFiltro] = useState(0);
    const estado = FILTROS_CAMPANAS[filtro]?.valor ?? null;
    const [canal, setCanal] = useState<CanalCampana | ''>('');
    const pag = usePaginacionCursor();
    const [lista, setLista] = useState<Lista | null>(null);
    const [error, setError] = useState<unknown>(null);
    const [intento, setIntento] = useState(0);
    const [abierto, setAbierto] = useState(busqueda.get('apartado') === 'resultados');
    const [resumen, setResumen] = useState<ResumenResultados | null>(null);
    const [nueva, setNueva] = useState(false);
    const [hecho, setHecho] = useState<string | null>(null);
    const periodo = useMemo(() => periodos()[0], []);
    const base = ruta.split('?')[0] ?? ruta;
    const elegida = busqueda.get('campana');

    useCabecera({
        ayuda: AYUDA.campanas,
        subtitulo: SUBTITULO.equipo,
        acciones: [
            ...(!movil ? [{ clave: 'ajustes', texto: 'Ajustes', icono: 'sliders-horizontal' as const, secundaria: true, onClick: abrirAjustes }] : []),
            ...(crea ? [{ clave: 'nueva', texto: 'Nueva campaña', icono: 'plus' as const, onClick: () => { setHecho(null); setNueva(true); } }] : []),
        ],
    });

    useEffect(() => {
        let vivo = true;
        ops.listCampaigns(negocio, { ...(estado === null ? {} : { status: [...estado] }), ...(canal === '' ? {} : { channel: canal }), ...(pag.cursor === null ? {} : { cursor: pag.cursor }) })
            .then((r) => {
                if (vivo) {
                    setLista(r);
                    setError(null);
                }
            })
            .catch((e: unknown) => {
                if (vivo) {
                    setError(e);
                }
            });
        return () => {
            vivo = false;
        };
    }, [ops, negocio, estado, canal, pag.cursor, intento]);

    useEffect(() => {
        if (!veResumen || periodo === undefined) {
            return;
        }
        let vivo = true;
        ops.getCampaignResultsSummary(negocio, { from: periodo.from, to: periodo.to })
            .then((r) => {
                if (vivo) {
                    setResumen(r);
                }
            })
            .catch(() => undefined);
        return () => {
            vivo = false;
        };
    }, [ops, negocio, veResumen, periodo, intento]);

    const abrir = (id: string | null) => {
        navegar(id === null ? base : `${base}?campana=${encodeURIComponent(id)}`, true);
    };
    const recargar = (texto?: string) => {
        setHecho(texto ?? null);
        setIntento((i) => i + 1);
    };

    if (error !== null) {
        const e = estadoDeError(error);
        if (e.tipo === 'no-contratado') {
            const propietario = membresia?.is_owner === true;
            return (
                <EstadoNoContratado
                    bloque="Mensajes y campañas"
                    incluye={['Campañas por email, WhatsApp y Telegram', 'Mensajes automáticos tras una visita', '2.000 envíos al mes incluidos']}
                    propietario={propietario}
                    onAnadir={propietario ? () => { navegar('/plan?block=mensajes_campanas'); } : null}
                    onInicio={() => { navegar('/'); }}
                    movil={movil}
                />
            );
        }
        return <EstadoError seccion="las campañas" codigoSoporte={e.codigoSoporte} onReintentar={() => { setIntento((i) => i + 1); }} movil={movil} />;
    }
    if (lista === null) {
        return <EstadoCargando seccion="las campañas" />;
    }

    const filtrada = estado !== null || canal !== '';
    const vacia = lista.data.length === 0 && !filtrada && !pag.hayAnterior;
    const t = resumen?.totals;
    const abrirFila = (f: Resultados) => {
        if (f.subject_kind === 'automation') {
            irA('automaticos', `regla=${encodeURIComponent(f.subject_id)}`);
        } else {
            abrir(f.subject_id);
        }
    };

    return (
        <div className="rv-ca-apartado">
            {hecho !== null && (
                <div className="rv-ca-hecho" role="status">
                    <span className="rv-ca-hecho__baldosa">
                        <Icono nombre="check" />
                    </span>
                    <span>{hecho}</span>
                </div>
            )}
            {!puede('campanas.campanas.crear') && (
                <p className="rv-ca-nota">
                    <Icono nombre="eye" />
                    <span>Con tu rol ves las campañas y cómo les va. Crearlas y enviarlas lo hacen el propietario y los administradores.</span>
                </p>
            )}
            {t !== undefined && !vacia && periodo !== undefined && (
                <div className="rv-cp-frase">
                    <span>
                        {`${periodo.etiqueta}: `}
                        <strong>{cifra(t.conversions)}</strong>
                        {` reservas y compras${t.conversions_amount !== undefined && t.conversions_amount.amount > 0 ? ` (${importe(t.conversions_amount)})` : ''} después de tus mensajes.`}
                    </span>
                    <Boton tipo="secundario" icono={abierto ? 'chevron-up' : 'chevron-down'} movil={movil} aria-expanded={abierto} onClick={() => { setAbierto((a) => !a); }}>
                        {abierto ? 'Ocultar resultados' : 'Ver resultados'}
                    </Boton>
                </div>
            )}
            {abierto && veResumen && !vacia && <Resumen ops={ops} negocio={negocio} movil={movil} socio={false} onAbrir={abrirFila} />}
            {vacia ? (
                <div className="rv-ca-vacia">
                    <span className="rv-ca-vacia__baldosa">
                        <Icono nombre="send" />
                    </span>
                    <strong>Todavía no has enviado ninguna campaña.</strong>
                    <span>{crea ? 'Escríbele a tus clientes con permiso para ofertas: por WhatsApp, Telegram o email, ahora o programada.' : 'Aquí verás las campañas del negocio y cómo les va.'}</span>
                    {crea && (
                        <Boton icono="plus" movil={movil} onClick={() => { setNueva(true); }}>
                            Nueva campaña
                        </Boton>
                    )}
                </div>
            ) : (
                <>
                    <div className="rv-cp-filtros">
                        <Filtros
                            etiqueta="Estado de la campaña"
                            items={FILTROS_CAMPANAS.map((f) => ({ etiqueta: f.etiqueta, cifra: cifraDeFiltro(f.valor, lista.counts) }))}
                            valor={filtro}
                            onCambio={(i) => { setFiltro(i); pag.reiniciar(); }}
                        />
                        <Desplegable
                            etiqueta="Canal"
                            valor={canal}
                            opciones={[{ valor: '', texto: 'Todos los canales' }, ...(Object.keys(CANAL) as CanalCampana[]).map((k) => ({ valor: k, texto: CANAL[k].nombre }))]}
                            onChange={(v) => { setCanal(v as CanalCampana | ''); pag.reiniciar(); }}
                        />
                    </div>
                    <Tabla
                        etiqueta="Campañas"
                        movil={movil}
                        vacia={
                            <div className="rv-ca-vacia">
                                <span className="rv-ca-vacia__baldosa">
                                    <Icono nombre="inbox" />
                                </span>
                                <strong>No hay campañas con estos filtros.</strong>
                                <span>Prueba con otro estado o canal.</span>
                            </div>
                        }
                        columnas={[
                            { clave: 'campana', titulo: 'Campaña', ancho: 'minmax(220px, 1.7fr)' },
                            { clave: 'cuando', titulo: 'Cuándo', ancho: 'minmax(130px, 0.9fr)' },
                            { clave: 'dest', titulo: 'Destinatarios', ancho: 'minmax(96px, 0.6fr)', alinear: 'derecha' },
                            { clave: 'res', titulo: 'Resultados', ancho: 'minmax(190px, 1.3fr)' },
                            { clave: 'estado', titulo: 'Estado', ancho: 'minmax(130px, 0.9fr)' },
                        ]}
                        filas={lista.data.map((c) => {
                            const cuando = cuandoDe(c);
                            const est = ESTADO_CAMPANA[c.status];
                            return {
                                clave: c.id,
                                etiqueta: c.name,
                                onAbrir: () => { abrir(c.id); },
                                celdas: {
                                    campana: (
                                        <span className="rv-en-celda">
                                            <strong>{c.name}</strong>
                                            <small>
                                                <Icono nombre={CANAL[c.channel].icono} /> {lineaCanal(c)}
                                            </small>
                                        </span>
                                    ),
                                    cuando: (
                                        <span className="rv-en-celda">
                                            <strong>{cuando[0]}</strong>
                                            <small>{cuando[1]}</small>
                                        </span>
                                    ),
                                    dest: destinatariosDe(c),
                                    res: resultadoDe(c),
                                    estado: (
                                        <span className="rv-en-celda">
                                            {est.chip === null ? <span>{est.texto}</span> : <Chip estado={est.chip}>{est.texto}</Chip>}
                                            {c.status === 'paused' && c.pause_reason != null && <small>{PAUSA[c.pause_reason].titulo}</small>}
                                        </span>
                                    ),
                                },
                            };
                        })}
                    />
                    <Paginacion hayAnterior={pag.hayAnterior} haySiguiente={lista.next_cursor !== null} onAnterior={pag.anterior} onSiguiente={() => { pag.siguiente(lista.next_cursor); }} movil={movil} />
                </>
            )}
            {elegida !== null && !nueva && (
                <FichaCampana
                    key={elegida}
                    {...props}
                    campanaId={elegida}
                    onCerrar={() => { abrir(null); }}
                    onCambio={(texto) => { recargar(texto); }}
                    onAbrirOtra={(id) => { recargar(); abrir(id); }}
                />
            )}
            {nueva && (
                <Editor
                    {...props}
                    campana={null}
                    onCerrar={(texto) => {
                        setNueva(false);
                        recargar(texto);
                    }}
                />
            )}
        </div>
    );
}

function lineaCanal(c: Campana): string {
    if (c.channel === 'email') {
        return `Email · ${c.content?.email_subject ?? 'sin asunto'}`;
    }
    if (c.channel === 'telegram') {
        return 'Telegram · Texto';
    }
    return `WhatsApp · ${c.content?.meta_template_id == null ? 'Sin plantilla' : 'Plantilla'}`;
}

function destinatariosDe(c: Campana): string {
    if (c.frozen_recipients != null) {
        return cifra(c.frozen_recipients);
    }
    return c.last_preview == null ? '—' : `~${cifra(c.last_preview.audience_size)}`;
}

